import { useState, useRef, useEffect, useId } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import './ImageUpload.css';

/**
 * @param {Object} props
 * @param {string} props.value - Current image URL or base64
 * @param {function(string): void} props.onChange
 * @param {string} [props.label='Image']
 * @param {boolean} [props.required=false]
 * @param {string} [props.accept='image/*']
 * @param {function(File): Promise<string>} [props.uploadImage] - If provided, file is uploaded and the returned URL is used instead of base64 (recommended for blogs/content to avoid DB size issues).
 */
function getYouTubeVideoId(url = '') {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') {
      return parsed.pathname.split('/').filter(Boolean)[0] || '';
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname === '/watch') return parsed.searchParams.get('v') || '';
      if (parsed.pathname.startsWith('/shorts/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
      if (parsed.pathname.startsWith('/embed/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
    }
    return '';
  } catch {
    return '';
  }
}

function getYouTubeEmbedUrl(url = '') {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return '';
  const q = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: videoId,
    controls: '0',
    modestbranding: '1',
    rel: '0',
    playsinline: '1'
  });
  return `https://www.youtube.com/embed/${videoId}?${q.toString()}`;
}

const ImageUpload = ({ value, onChange, label = 'Image', required = false, accept = 'image/*', uploadImage }) => {
  const inputId = useId();
  const [preview, setPreview] = useState(value || '');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(value || '');
  }, [value]);
  const { showToast } = useToast();
  const allowVideo = accept.includes('video/*');
  const isVideoPreview = /(\.mp4|\.webm|\.ogg|\.mov|\.m4v)(?:[?#].*)?$/i.test(preview || '') || String(preview || '').startsWith('data:video/');
  const youTubeEmbedUrl = allowVideo ? getYouTubeEmbedUrl(preview || '') : '';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !(allowVideo && isVideo)) {
      showToast({ message: allowVideo ? 'Please select an image or video file' : 'Please select an image file', severity: 'warning' });
      return;
    }

    setIsUploading(true);

    try {
      if (typeof uploadImage === 'function') {
        const url = await uploadImage(file);
        if (url) {
          setPreview(url);
          onChange(url);
        } else {
          showToast({ message: 'Upload succeeded but no URL returned', severity: 'warning' });
        }
      } else {
        // Fallback: convert to base64 (not recommended for large images / blogs)
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          setPreview(base64String);
          onChange(base64String);
          setIsUploading(false);
        };
        reader.onerror = () => {
          showToast({ message: 'Error reading file', severity: 'error' });
          setIsUploading(false);
        };
        reader.readAsDataURL(file);
        return;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast({ message: error.response?.data?.message || 'Error uploading image', severity: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlInput = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">
        {label} {required && <span className="required">*</span>}
      </label>
      
      {/* Upload Area */}
      {!preview ? (
        <div className="image-upload-area">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="image-upload-input"
            id={inputId}
            disabled={isUploading}
          />
          <label htmlFor={inputId} className="image-upload-dropzone">
            <Upload size={32} className="upload-icon" />
            <p className="upload-text">
              {isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="upload-hint">
              {allowVideo ? 'Images or videos' : 'PNG, JPG, GIF'}
            </p>
          </label>
        </div>
      ) : (
        <div className="image-preview-container">
          <div className="image-preview">
            {youTubeEmbedUrl ? (
              <iframe
                src={youTubeEmbedUrl}
                title="Media preview"
                className="preview-image"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : isVideoPreview ? (
              <video src={preview} className="preview-image" controls muted playsInline />
            ) : (
              <img src={preview} alt="Preview" className="preview-image" />
            )}
            <button
              type="button"
              className="remove-image-btn"
              onClick={handleRemove}
              title="Remove image"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* URL Input Alternative */}
      <div className="image-url-input">
        <label className="url-label">{allowVideo ? 'Or enter image/video URL:' : 'Or enter image URL:'}</label>
        <div className="url-input-wrapper">
          <ImageIcon size={18} className="url-icon" />
          <input
            type="url"
            value={preview || ''}
            onChange={handleUrlInput}
            placeholder={allowVideo ? 'https://example.com/file.jpg, .mp4, or YouTube URL' : 'https://example.com/image.jpg'}
            className="url-input"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
